import React from 'react';
import { reportDialerEvent } from '../lib/dialerTelemetry';

/**
 * Recovers from the "failed to load a lazily-imported chunk" error that happens
 * when a user has the SPA open in a tab, a new version deploys, and they then
 * navigate to a lazy route whose old chunk is no longer reachable. index.html is
 * served `no-cache`, so a single reload pulls the current index.html → current
 * chunk names and the navigation succeeds.
 *
 * Design notes:
 * - Only chunk-load failures auto-reload. Any OTHER render crash used to be
 *   reloaded too (with the message "המערכת עודכנה"), which masked real bugs as
 *   "an update" and, on a deterministic crash, reloaded once and then showed the
 *   same misleading text. Those now render an honest error fallback and are
 *   reported to the server (POST /calls/client-event, event=render_crash) with
 *   the stack + component stack so they can actually be fixed.
 * - Chunk detection is deliberately BROAD (name + several browser/bundler
 *   wordings): an over-narrow regex is what white-screened an earlier version.
 * - A timestamp guard (not a one-time flag) prevents reload loops while still
 *   allowing recovery from a *later*, unrelated stale-chunk error in a long
 *   session: we only auto-reload if we haven't already reloaded in the last few
 *   seconds. If the error survives that reload, we show a manual fallback.
 * - Donor-facing routes are eager (not lazy), so this only ever triggers for
 *   logged-in users navigating the app — never on the public donation flow.
 */
const RELOAD_TS = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 8000;

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script|module script failed|Failed to fetch|NetworkError when attempting to fetch|error loading dynamically imported|Unexpected token '<'|MIME type/i;

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_RE.test(String(e.message || ''));
}

interface State {
  error: Error | null;
  kind: 'chunk' | 'crash' | null;
}

class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null, kind: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, kind: isChunkLoadError(error) ? 'chunk' : 'crash' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (!isChunkLoadError(error)) {
      // A real render crash — never reload it away. Report so it's visible
      // server-side (CloudWatch + /admin/logs) and leave the fallback up.
      reportDialerEvent('render_crash', {
        message: String(error?.message || error).slice(0, 500),
        stack: String(error?.stack || '').slice(0, 1500),
        componentStack: String(info?.componentStack || '').slice(0, 1500),
      });
      return;
    }
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_TS) || '0'); } catch { /* private mode */ }
    const now = Date.now();
    // Stale chunk after a deploy — reload once to pull the fresh build. If we
    // reloaded moments ago and it's STILL failing, fall through to the manual
    // fallback (no loop).
    if (now - last > RELOAD_WINDOW_MS) {
      try { sessionStorage.setItem(RELOAD_TS, String(now)); } catch { /* private mode */ }
      window.location.reload();
    }
  }

  render() {
    if (this.state.error) {
      const isChunk = this.state.kind === 'chunk';
      // Never rethrow to a blank page. For a stale chunk either a reload is
      // already in flight (from componentDidCatch) and this flashes for a
      // frame, or the reload didn't fix it and the user gets an explicit retry.
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4 text-center p-6">
          <p className="text-gray-700">
            {isChunk
              ? 'המערכת עודכנה. נטען מחדש כדי לקבל את הגרסה החדשה…'
              : 'אירעה שגיאה בתצוגת העמוד. רענון הדף אמור לפתור זאת; אם זה חוזר — פנו לתמיכה.'}
          </p>
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white"
            onClick={() => {
              try { sessionStorage.removeItem(RELOAD_TS); } catch { /* private mode */ }
              window.location.reload();
            }}
          >
            רענון
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
